import React, { useState, useEffect, useRef } from 'react';
import { Search, ShoppingCart, Truck, Wallet, ChevronRight, Sparkles, ArrowRight, Zap } from 'lucide-react';

const steps = [
  {
    id: 1,
    icon: Search,
    title: 'Находите',
    subtitle: 'товар',
    lines: ['Который уже покупают.', 'Не придумываете.', 'Берёте готовое.'],
    color: 'amber',
    gradient: 'from-amber-500 to-orange-600',
    emoji: '🔍'
  },
  {
    id: 2,
    icon: ShoppingCart,
    title: 'Закупаете',
    subtitle: 'дешевле',
    lines: ['У поставщика — дешевле.', 'На Amazon — дороже.', 'Разница — ваша.'],
    color: 'emerald',
    gradient: 'from-emerald-500 to-teal-600',
    emoji: '🛒'
  },
  {
    id: 3,
    icon: Truck,
    title: 'Отправляете',
    subtitle: 'один раз',
    lines: ['На склад Amazon.', 'Один раз.', 'Всё.'],
    color: 'blue',
    gradient: 'from-blue-500 to-indigo-600',
    emoji: '📦'
  },
  {
    id: 4,
    icon: Wallet,
    title: 'Получаете',
    subtitle: 'прибыль',
    lines: ['Amazon хранит.', 'Amazon продаёт.', 'Amazon доставляет.'],
    color: 'violet',
    gradient: 'from-violet-500 to-purple-600',
    emoji: '💰'
  },
];

const HowItWorksSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [hoveredStep, setHoveredStep] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-rotate when no hover
  useEffect(() => {
    if (hoveredStep !== null) return;
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [hoveredStep]);

  const currentStep = hoveredStep !== null ? hoveredStep : activeStep;

  return (
    <section 
      ref={sectionRef}
      className="relative py-20 sm:py-28 overflow-hidden bg-[#030304]"
      id="how-it-works"
    >
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-amber-500/[0.03] rounded-full blur-[200px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-violet-500/[0.03] rounded-full blur-[150px]" />
        
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-amber-500/30 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 mb-6 backdrop-blur-xl">
            <Zap className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300 font-bold text-sm tracking-wide">КАК ЭТО РАБОТАЕТ</span>
          </div>
          
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 leading-tight">
            <span className="text-white">4 шага к </span>
            <span className="relative">
              <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 bg-clip-text text-transparent">
                прибыли
              </span>
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                <path d="M2 10C50 2 150 2 198 10" stroke="url(#underline-gradient)" strokeWidth="4" strokeLinecap="round"/>
                <defs>
                  <linearGradient id="underline-gradient" x1="0" y1="0" x2="200" y2="0">
                    <stop stopColor="#f59e0b"/>
                    <stop offset="0.5" stopColor="#f97316"/>
                    <stop offset="1" stopColor="#f59e0b"/>
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h2>
          
          <p className="text-xl sm:text-2xl text-zinc-400 max-w-2xl mx-auto">
            <span className="text-white font-semibold">Amazon — витрина.</span> Миллионы покупателей ждут.
          </p>
        </div>

        {/* Progress indicator */}
        <div className={`flex justify-center gap-3 mb-12 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          {steps.map((step, i) => (
            <button
              key={i}
              onClick={() => { setActiveStep(i); setHoveredStep(null); }}
              className={`relative h-2 rounded-full transition-all duration-500 ${
                i === currentStep ? 'w-12 bg-gradient-to-r ' + step.gradient : 'w-2 bg-zinc-700 hover:bg-zinc-600'
              }`}
            >
              {i === currentStep && (
                <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${step.gradient} animate-pulse blur-sm`} />
              )}
            </button>
          ))}
        </div>

        {/* Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-16">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === index;
            
            return (
              <div
                key={step.id}
                className={`group relative cursor-pointer transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
                onMouseEnter={() => setHoveredStep(index)}
                onMouseLeave={() => setHoveredStep(null)}
                onClick={() => setActiveStep(index)}
              >
                {/* Glow */}
                <div className={`absolute -inset-3 bg-gradient-to-br ${step.gradient} rounded-[2rem] blur-2xl transition-all duration-700 ${
                  isActive ? 'opacity-40 scale-105' : 'opacity-0 scale-100'
                }`} />
                
                {/* Card */}
                <div className={`relative h-full rounded-3xl border-2 overflow-hidden transition-all duration-500 ${
                  isActive 
                    ? `bg-gradient-to-b from-zinc-800/90 to-zinc-900/95 border-${step.color}-500/50 shadow-2xl -translate-y-2` 
                    : 'bg-zinc-900/50 border-zinc-800/50 hover:border-zinc-700'
                }`}>
                  
                  {/* Top accent line */}
                  <div className={`h-1.5 bg-gradient-to-r ${step.gradient} transition-all duration-500 ${
                    isActive ? 'opacity-100' : 'opacity-0'
                  }`} />
                  
                  <div className="p-6 sm:p-8">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-xl transition-all duration-500 ${
                        isActive ? 'scale-110 rotate-3' : 'group-hover:scale-105'
                      }`}>
                        <Icon className="w-8 h-8 text-white" />
                        {isActive && (
                          <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${step.gradient} animate-ping opacity-30`} />
                        )}
                      </div>
                      
                      <span className={`text-7xl font-black transition-all duration-500 ${
                        isActive 
                          ? `bg-gradient-to-br ${step.gradient} bg-clip-text text-transparent` 
                          : 'text-zinc-800'
                      }`}>
                        {step.id}
                      </span>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-3xl font-black text-white mb-1">{step.title}</h3>
                    <p className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
                      isActive ? `text-${step.color}-400` : 'text-zinc-500'
                    }`}>{step.subtitle}</p>
                    
                    {/* Content */}
                    <div className="space-y-2">
                      {step.lines.map((line, i) => (
                        <p 
                          key={i}
                          className={`text-base transition-all duration-500 ${
                            isActive ? 'text-zinc-200' : 'text-zinc-500'
                          }`}
                          style={{ transitionDelay: `${i * 50}ms` }}
                        >
                          {line}
                        </p>
                      ))}
                    </div>
                    
                    {/* Arrow */}
                    <div className={`absolute bottom-6 right-6 w-12 h-12 rounded-full bg-gradient-to-r ${step.gradient} flex items-center justify-center transition-all duration-500 ${
                      isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                    }`}>
                      <ArrowRight className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-500/50" />
            <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-500/50" />
          </div>
          
          <p className="text-4xl sm:text-5xl font-black mb-8">
            <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 bg-clip-text text-transparent">
              Вы забираете прибыль
            </span>
          </p>
          
          <a 
            href="#pricing"
            className="group inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-black text-xl rounded-2xl shadow-2xl shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-1 active:scale-[0.98] transition-all duration-300"
            style={{ touchAction: 'manipulation' }}
          >
            <span>Хочу так же</span>
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
